import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import { ApiError, friendlyMessage } from "./accountApi";
import {
  deleteDesktopApp,
  deleteDesktopRelease,
  downloadRelease as downloadReleaseFile,
  listDesktopApps,
  publishRelease,
  registerDesktopApp,
  updateDesktopApp,
  uploadDesktopIcon,
} from "./downloadsApi";
import type {
  DesktopApp,
  DesktopPlatform,
  DesktopRelease,
  RegisterAppRequest,
  UpdateAppRequest,
} from "../models/downloads";
import { detectPlatform } from "../models/downloads";

/** In-flight byte transfer for one file, so the view can render a real progress bar. */
export interface Transfer {
  label: string;
  sent: number;
  total: number;
}

/**
 * Outcome of a mutating action. The message is returned as well as pushed into `error`, because a
 * caller inside a dialog needs it in the same turn — reading `error` off the controller right after
 * awaiting would still see the previous render's value.
 */
export interface ActionResult {
  ok: boolean;
  error: string | null;
}

/**
 * Korean wording for the 다운로드 error codes an admin can actually trigger. The platform's own
 * messages are English developer strings ("desktop app key already in use: …"); these are the two
 * cases a person hits by hand, so they get an instruction instead of a diagnosis.
 */
const DOWNLOAD_ERROR_KO: Record<string, string> = {
  "DL-0002": "같은 이름(식별 키)의 앱이 이미 있습니다. 다른 이름이나 식별 키를 쓰세요.",
  "DL-0004": "해당 플랫폼에 같은 버전이 이미 등록돼 있습니다. 버전 번호를 올리거나 기존 버전을 먼저 삭제하세요.",
};

function downloadsMessage(e: unknown): string {
  if (e instanceof ApiError && e.code && DOWNLOAD_ERROR_KO[e.code]) return DOWNLOAD_ERROR_KO[e.code];
  return friendlyMessage(e);
}

/**
 * Loads and mutates the 다운로드 centre (자료실). Reads are open to every signed-in user; the write
 * actions are only surfaced to platform admins by the view (and rejected by the backend otherwise).
 */
export function useDownloads() {
  const { status, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [apps, setApps] = useState<DesktopApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upload, setUpload] = useState<Transfer | null>(null);
  const [download, setDownload] = useState<Transfer | null>(null);
  // The visitor's OS decides which build the primary 다운로드 button offers.
  const platform = useMemo(detectPlatform, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setApps(await listDesktopApps(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (status === "authenticated") void reload();
  }, [status, reload]);

  /** Runs an action, surfacing any failure as `error` and handing the message back to the caller. */
  const guard = useCallback(async (action: () => Promise<void>): Promise<ActionResult> => {
    setError(null);
    try {
      await action();
      return { ok: true, error: null };
    } catch (e) {
      const message = downloadsMessage(e);
      setError(message);
      return { ok: false, error: message };
    }
  }, []);

  const download1 = useCallback(async (release: DesktopRelease) => {
    setDownload({ label: release.filename, sent: 0, total: release.size });
    const result = await guard(async () => {
      await downloadReleaseFile(getToken, release, (sent, total) =>
        setDownload({ label: release.filename, sent, total }));
    });
    setDownload(null);
    // The counter moved server-side; refresh so the admin's 받은 수 stays truthful.
    if (result.ok) void reload();
    return result;
  }, [getToken, guard, reload]);

  const register = useCallback((req: RegisterAppRequest) =>
    guard(async () => {
      await registerDesktopApp(getToken, req);
      await reload();
    }), [getToken, guard, reload]);

  const update = useCallback((id: string, req: UpdateAppRequest) =>
    guard(async () => {
      await updateDesktopApp(getToken, id, req);
      await reload();
    }), [getToken, guard, reload]);

  const uploadIcon = useCallback((appId: string, file: File) =>
    guard(async () => {
      await uploadDesktopIcon(getToken, appId, file);
      await reload();
    }), [getToken, guard, reload]);

  const removeApp = useCallback((id: string) =>
    guard(async () => {
      await deleteDesktopApp(getToken, id);
      await reload();
    }), [getToken, guard, reload]);

  const removeRelease = useCallback((id: string) =>
    guard(async () => {
      await deleteDesktopRelease(getToken, id);
      await reload();
    }), [getToken, guard, reload]);

  const publish = useCallback(async (
    appId: string,
    file: File,
    req: { version: string; platform: DesktopPlatform; releaseNotes?: string },
  ) => {
    setUpload({ label: file.name, sent: 0, total: file.size });
    const result = await guard(async () => {
      await publishRelease(getToken, appId, file, req, (sent, total) =>
        setUpload({ label: file.name, sent, total }));
      await reload();
    });
    setUpload(null);
    return result;
  }, [getToken, guard, reload]);

  return {
    apps,
    loading,
    error,
    platform,
    upload,
    download,
    getToken,
    reload,
    clearError: () => setError(null),
    downloadRelease: download1,
    register,
    update,
    uploadIcon,
    removeApp,
    removeRelease,
    publish,
  };
}

export type DownloadsController = ReturnType<typeof useDownloads>;
